import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Copy, Eye, EyeOff, Trash2, ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, Info, KeyRound, ArrowRight, Search, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { copyToClipboard } from "../../lib/clipboard";
import { Input } from "../ui/input";
import { durationBucket, guessesBucket } from "../../lib/passwordIntel/engine";
import { analyze } from "../../lib/passwordIntel/client";
import { describeSegments, detectedPatterns } from "../../lib/passwordIntel/patterns";
import { unicodeWarnings, codePointLength } from "../../lib/passwordIntel/unicode";
import { checkBreaches, activeBreachSources } from "../../lib/passwordIntel/breach";
import { globalVerdict, needsReplacement, SCORE_LEVEL } from "../../lib/passwordIntel/verdict";
import { verdictShareText } from "../../lib/passwordIntel/share";
import { exactReadableEntropy } from "../../lib/readable";
import { getLocaleConfig } from "../../i18n/locales";

const L = (t, k) => t(k, { returnObjects: true });
const TONE = {
  compromised: "border-red-300 bg-red-50 text-red-700", veryWeak: "border-red-200 bg-red-50 text-red-700",
  weak: "border-orange-200 bg-orange-50 text-orange-700", fair: "border-amber-200 bg-amber-50 text-amber-700",
  strong: "border-emerald-200 bg-emerald-50 text-emerald-700", veryStrong: "border-emerald-300 bg-emerald-50 text-emerald-800",
};
const BAR = ["bg-red-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600"];
const CTX_FIELDS = ["service", "username", "email", "company"];

const Card = ({ title, children, testid, icon: Icon = Info }) => (
  <section data-testid={testid} className="glass rounded-2xl p-5 sm:p-6 mb-4">
    <h2 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2 text-base md:text-lg"><Icon className="w-4 h-4 text-emerald-600" />{title}</h2>
    <div className="text-sm text-slate-600 leading-relaxed space-y-2">{children}</div>
  </section>
);

const Details = ({ title, children, testid }) => (
  <details data-testid={testid} className="group rounded-lg border border-slate-200 bg-white p-3 mt-2">
    <summary className="cursor-pointer list-none font-medium text-slate-800 flex items-center justify-between gap-3 text-sm">
      {title}<span className="text-slate-400 group-open:rotate-180 transition-transform">▾</span>
    </summary>
    <div className="mt-3 text-sm text-slate-600 space-y-2">{children}</div>
  </details>
);

// Synthèse : 3 dimensions indépendantes + verdict (la compromission prime toujours).
const ShareVerdict = ({ t, verdict, result, breach, patterns }) => {
  const onCopy = async () => {
    const text = verdictShareText(t, { verdict, score: result.score, breachStatus: breach.status, patternCount: patterns.length, url: `${window.location.origin}/tester-mot-de-passe` });
    if (await copyToClipboard(text)) toast.success(t("share.copied"));
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Button data-testid="pwtest-share-verdict-btn" size="sm" variant="outline" onClick={onCopy} className="bg-white/80 border-current/20"><Copy className="w-4 h-4 mr-2" />{t("share.button")}</Button>
      <span className="text-[11px] opacity-80">{t("share.help")}</span>
    </div>
  );
};

const Synthesis = ({ t, verdict, result, breach, patterns }) => (
  <div data-testid="pwtest-synthesis" className={`rounded-2xl border-2 p-5 mb-4 ${TONE[verdict]}`}>
    <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{t("verdictTitle")}</div>
    <div data-testid="pwtest-verdict" className="font-heading text-2xl font-extrabold mt-1">{t(`verdict.${verdict}`)}</div>
    <p data-testid="pwtest-key-message" className="text-sm mt-1">
      {verdict === "compromised" ? t("verdictHint.compromised") : needsReplacement(verdict) ? t("verdictHint.replace") : breach.status === "clean" ? t("verdictHint.good") : t("verdictHint.pending")}
    </p>
    <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 text-sm">
      <div className="rounded-lg bg-white/70 p-2"><dt className="text-xs opacity-70">{t("summary.resistance")}</dt><dd data-testid="pwtest-strength-label" className="font-semibold">{t(`verdict.${SCORE_LEVEL[result.score]}`)}</dd></div>
      <div className="rounded-lg bg-white/70 p-2"><dt className="text-xs opacity-70">{t("summary.exposure")}</dt><dd data-testid="pwtest-exposure-summary" className="font-semibold">{t(`summary.exposureState.${breach.status}`)}</dd></div>
      <div className="rounded-lg bg-white/70 p-2"><dt className="text-xs opacity-70">{t("summary.patterns")}</dt><dd data-testid="pwtest-patterns-summary" className="font-semibold">{patterns.length ? t("summary.countPatterns", { count: patterns.length }) : t("summary.noMajor")}</dd></div>
    </dl>
    <ShareVerdict t={t} verdict={verdict} result={result} breach={breach} patterns={patterns} />
  </div>
);

const Resistance = ({ t, result, exact }) => (
  <Card title={t("resistanceTitle")} testid="pwtest-strength" icon={ShieldCheck}>
    <div data-testid="pwtest-strength-gauge" className="flex gap-1">
      {[0, 1, 2, 3, 4].map((i) => <div key={i} className={`h-2 flex-1 rounded-full ${i <= result.score ? BAR[result.score] : "bg-slate-200"}`} />)}
    </div>
    <p className="text-xs text-slate-500">{t("resistanceHelp")}</p>
    <p data-testid="pwtest-guesses">{t("guesses", { bucket: t(`guessBucket.${guessesBucket(result.guessesLog10)}`), log: Math.round(result.guessesLog10) })}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pwtest-crack">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-2"><div className="text-xs text-slate-500">{t("online")}</div><div className="font-semibold text-slate-800">{t(`duration.${durationBucket(result.crackSeconds.online)}`)}</div></div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-2"><div className="text-xs text-slate-500">{t("offline")}</div><div className="font-semibold text-slate-800">{t(`duration.${durationBucket(result.crackSeconds.offlineFast)}`)}</div></div>
    </div>
    <p className="text-[11px] text-slate-500">{t("crackDisclaimer")}</p>
    {exact && (
      <div data-testid="pwtest-generation-entropy" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <div className="font-semibold text-emerald-800">{t("generationTitle")}</div>
        <p>{t("generationText", { bits: exact.bits.toFixed(1), locale: getLocaleConfig(exact.locale).nativeLabel })}</p>
        <p data-testid="pwtest-generation-provenance" className="text-xs text-slate-600">{t("generationProvenance")}</p>
        <p className="text-xs text-slate-500">{t("generationDiff")}</p>
      </div>
    )}
  </Card>
);

const Why = ({ t, result, patterns }) => {
  const segs = describeSegments(result.sequence);
  return (
    <Card title={t("whyTitle")} testid="pwtest-why" icon={Search}>
      <div className="flex flex-wrap gap-1.5 ph-no-capture" data-testid="pwtest-segments">
        {segs.map((s, i) => (
          <span key={i} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs">
            <span className="font-mono-token text-slate-900 break-all">{s.token}</span>
            <span className="text-slate-500"> · {t(`kind.${s.kind}`)}{s.l33t ? `, ${t("flag.l33t")}` : ""}{s.reversed ? `, ${t("flag.reversed")}` : ""}</span>
          </span>
        ))}
      </div>
      {patterns.length ? (
        <ul data-testid="pwtest-suggestions" className="list-disc pl-5 space-y-1">
          {patterns.map((p) => <li key={p} data-testid={`pwtest-pattern-${p}`}>{t(`patterns.${p}`)}</li>)}
          {result.warning && <li>{t(`zx.${result.warning}`)}</li>}
          {result.suggestions.map((s) => <li key={s}>{t(`zx.${s}`)}</li>)}
        </ul>
      ) : <p>{t("whyNone")}</p>}
    </Card>
  );
};

const Breach = ({ t, breach, onCheck, disabled, activeSources }) => (
  <Card title={t("breachTitle")} testid="pwtest-hibp-result" icon={ShieldQuestion}>
    <p data-testid={`pwtest-breach-${breach.status}`} className={breach.status === "compromised" ? "font-semibold text-red-700" : ""}>
      {breach.status === "compromised" && <ShieldAlert data-testid="pwtest-hibp-compromised" className="inline w-4 h-4 mr-1" />}
      {breach.status === "unavailable" && <AlertTriangle data-testid="pwtest-hibp-error" className="inline w-4 h-4 mr-1 text-amber-600" />}
      {t(`breach.${breach.status}`, { consulted: breach.consulted, total: breach.total })}
    </p>
    {breach.status === "clean" && <p className="text-xs text-slate-500">{t("breach.cleanNote")}</p>}
    {breach.results?.length > 0 && (
      <div data-testid="pwtest-sources" className="text-xs text-slate-500">
        <div className="font-medium text-slate-600">{t("sources")}</div>
        <ul className="mt-1 space-y-0.5">
          {breach.results.map((r) => (
            <li key={r.provider} data-testid={`pwtest-source-${r.provider}`} data-status={r.status !== "ok" ? "error" : r.found ? "found" : "clean"}
              className={r.status === "ok" && r.found ? "text-red-700 font-semibold" : ""}>
              {t(`provider.${r.provider}`)} — {r.status !== "ok" ? t("providerError")
                : !r.found ? t("sourceClean")
                  : Number.isInteger(r.count) && r.count > 0 ? t("sourceFoundCount", { count: r.count }) : t("sourceFound")}
            </li>
          ))}
        </ul>
      </div>
    )}
    <Button data-testid="pwtest-hibp-check-btn" onClick={onCheck} disabled={disabled || breach.status === "loading"} variant="outline" className="border-slate-200">
      {breach.status === "loading" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
      {breach.status === "idle" ? t("breachCheck") : t("breachRecheck")}
    </Button>
    <p data-testid="pwtest-breach-privacy" className="text-[11px] text-slate-500">{t("breachPrivacy")}{" "}
      {activeSources?.length > 0 && t("activeSources", { list: activeSources.map((id) => t(`provider.${id}`)).join(", ") })}</p>
  </Card>
);

export const PasswordTesterPage = () => {
  const { t, i18n } = useTranslation("tester");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [ctx, setCtx] = useState({});
  const [result, setResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [breach, setBreach] = useState({ status: "idle" });
  const [activeSources, setActiveSources] = useState(null);
  useEffect(() => { activeBreachSources().then(setActiveSources).catch(() => setActiveSources([])); }, []);
  const runId = useRef(0);

  // Analyse locale débouncée (≈250 ms). Aucune donnée ne quitte le navigateur.
  useEffect(() => {
    if (!password) { setResult(null); return undefined; }
    setAnalyzing(true);
    const id = ++runId.current;
    const h = setTimeout(async () => {
      try {
        const r = await analyze(password, ctx, i18n.language);
        if (id === runId.current) setResult(r);
      } catch (e) {
        if (id === runId.current) setResult(null);
      } finally {
        if (id === runId.current) setAnalyzing(false);
      }
    }, 250);
    return () => clearTimeout(h);
  }, [password, ctx, i18n.language]);

  useEffect(() => { setBreach({ status: "idle" }); }, [password]);

  const runBreach = useCallback(async () => {
    if (!password) return;
    setBreach({ status: "loading" });
    try { setBreach(await checkBreaches(password)); } catch (e) { setBreach({ status: "unavailable", results: [], consulted: 0, total: 0 }); }
  }, [password]);

  const clear = () => { setPassword(""); setCtx({}); setResult(null); setBreach({ status: "idle" }); setShow(false); };

  const patterns = useMemo(() => (result ? detectedPatterns(password, result.sequence) : []), [result, password]);
  const verdict = result ? globalVerdict({ score: result.score, breach, patterns }) : null;
  const exact = useMemo(() => exactReadableEntropy(password, i18n.language), [password, i18n.language]);
  const uni = unicodeWarnings(password);

  return (
    <div data-testid="password-tester-page" className="max-w-3xl mx-auto py-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6 text-emerald-600" /></div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{t("title")}</h1>
      </div>
      <div className="text-base text-slate-600 leading-relaxed mb-6 space-y-3">{L(t, "intro").map((p) => <p key={p}>{p}</p>)}</div>

      <div className="glass rounded-2xl p-5 sm:p-6 mb-4">
        <label htmlFor="pwtest-field" className="text-sm font-medium text-slate-700">{t("inputLabel")}</label>
        <div className="relative mt-1">
          <Input id="pwtest-field" data-testid="pwtest-input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} name="securepass-pwtest-field" data-1p-ignore data-lpignore="true"
            placeholder={t("placeholder")} className="ph-no-capture bg-white border-slate-200 pr-24" />
          <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button type="button" data-testid="pwtest-toggle-visibility" aria-label={show ? t("hide") : t("show")} onClick={() => setShow((s) => !s)} className="p-2 text-slate-500 hover:text-slate-900">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            <button type="button" data-testid="pwtest-clear" aria-label={t("clear")} onClick={clear} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
        {password && (
          <p data-testid="pwtest-length" className="text-xs text-slate-500 mt-2">
            {t("length", { count: codePointLength(password) })}{result?.truncated ? ` ${t("truncated")}` : ""}{analyzing ? ` · ${t("analyzing")}` : ""}
          </p>
        )}
        {uni.length > 0 && (
          <div data-testid="pwtest-unicode-warnings" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            <b>{t("unicode.title")}</b> — {uni.map((w) => t(`unicode.${w}`)).join(" ")} {t("unicode.note")}
          </div>
        )}
        <Details title={t("contextTitle")} testid="pwtest-context">
          <p className="text-xs text-slate-500">{t("contextHelp")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CTX_FIELDS.map((f) => (
              <Input key={f} data-testid={`pwtest-ctx-${f}`} value={ctx[f] || ""} autoComplete="off" placeholder={t(`ctx.${f}`)} aria-label={t(`ctx.${f}`)}
                onChange={(e) => setCtx((c) => ({ ...c, [f]: e.target.value }))} className="ph-no-capture bg-white border-slate-200" />
            ))}
          </div>
        </Details>
      </div>

      {result && verdict && (
        <>
          <Synthesis t={t} verdict={verdict} result={result} breach={breach} patterns={patterns} />
          <Breach t={t} breach={breach} onCheck={runBreach} disabled={!password} activeSources={activeSources} />
          <Resistance t={t} result={result} exact={exact} />
          <Why t={t} result={result} patterns={patterns} />
          <Details title={t("detailsTitle")} testid="pwtest-details">
            <div className="font-medium">{t("advancedCrack")}</div>
            <ul className="text-xs space-y-1">{Object.entries(result.crackSeconds).map(([k, s]) => <li key={k}>{t("pair", { label: t(`scenario.${k}`), value: t(`duration.${durationBucket(s)}`) })}</li>)}</ul>
            <p className="text-xs">{t("resistanceHelp")}</p>
          </Details>
        </>
      )}

      <Card title={t("adviceTitle")} testid="pwtest-advice" icon={KeyRound}>
        <ul className="list-disc pl-5 space-y-1">
          {verdict === "compromised" && <li className="text-red-700 font-medium">{t("advice.compromised")}</li>}
          <li>{t("advice.length")}</li><li>{t("advice.unique")}</li><li>{t("advice.mfa")}</li>
        </ul>
        <p className="font-medium text-slate-800">{t("key")}</p>
      </Card>

      {(!verdict || needsReplacement(verdict)) && (
        <div data-testid="pwtest-generator-cta" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 mb-4">
          <Link data-testid="pwtest-generator-link" to="/generateur" className="inline-flex items-center gap-2 font-semibold text-emerald-700 hover:text-emerald-600">
            {t("cta")} <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-slate-500 mt-1">{t("ctaHelp")}</p>
        </div>
      )}

      <Card title={t("privacyTitle")} testid="pwtest-privacy" icon={ShieldCheck}>
        <ul className="list-disc pl-5 space-y-1">{L(t, "privacy").map((p) => <li key={p}>{p}</li>)}</ul>
      </Card>
    </div>
  );
};

export default PasswordTesterPage;
